// Shared visual tokens for the public, "sleek dark + glow" spectator view:
// keyframe animations, the hero gradient, and a glowing card surface. Keeping
// them in one place keeps the look cohesive across the groups / bracket / ranks
// sections and the shell. Uses Emotion's `keyframes` (a direct dependency) so
// the animations interpolate cleanly into MUI `sx` values.

import { keyframes } from '@emotion/react';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

/** Soft, breathing halo for the "live" status dot. */
export const livePulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(74,222,128,0.55); }
  70%  { box-shadow: 0 0 0 8px rgba(74,222,128,0); }
  100% { box-shadow: 0 0 0 0 rgba(74,222,128,0); }
`;

/** Gentle upward fade used when sections mount. */
export const rise = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/** Layered glow behind the hero header (crimson from the left, violet from the right). */
export const HERO_GRADIENT =
  'radial-gradient(130% 150% at 0% 0%, rgba(244,63,94,0.22) 0%, rgba(244,63,94,0) 46%),' +
  'radial-gradient(130% 150% at 100% 0%, rgba(168,85,247,0.18) 0%, rgba(168,85,247,0) 52%)';

/** Brand gradient for badges, accents and the trophy chip. */
export const ACCENT_GRADIENT = 'linear-gradient(135deg, #f43f5e 0%, #a855f7 100%)';

/**
 * Frosted, elevated card surface. When `active` (e.g. a completed group or a
 * decided match) it gains a crimson rim-light and a stronger glow so finished
 * sections read as "settled".
 */
export function glowCard(active = false): SystemStyleObject<Theme> {
  return {
    position: 'relative',
    borderRadius: 3,
    border: '1px solid',
    borderColor: active ? 'rgba(244,63,94,0.45)' : 'rgba(255,255,255,0.08)',
    backgroundColor: 'background.paper',
    backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.035), rgba(255,255,255,0))',
    boxShadow: active
      ? '0 14px 36px -14px rgba(244,63,94,0.40), inset 0 1px 0 rgba(255,255,255,0.05)'
      : '0 16px 34px -20px rgba(0,0,0,0.75), inset 0 1px 0 rgba(255,255,255,0.04)',
    transition: 'box-shadow .25s ease, border-color .25s ease',
  };
}
